import React, { useEffect, useState } from "react";
import axios from "axios";
import { analyticsHandler } from "./eventLogger.js";

const MODULE_NAME = "111_user_pattern_settings";
const screen = "pg_patterns";

// --- Toggle Switch ---
function ToggleSwitch({ checked, onChange, label }) {
    return (
        <label style={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            marginLeft: 8
        }}>
            <span style={{ marginRight: 8, fontWeight: 600 }}>{label}</span>
            <span style={{
                position: "relative",
                display: "inline-block",
                width: 44,
                height: 24
            }}>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => onChange(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                    position: "absolute",
                    cursor: "pointer",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: checked ? "#1976d2" : "#888",
                    borderRadius: 24,
                    transition: "background 0.2s"
                }} />
                <span style={{
                    position: "absolute",
                    left: checked ? 22 : 2,
                    top: 2,
                    width: 20,
                    height: 20,
                    background: "#fff",
                    borderRadius: "50%",
                    transition: "left 0.2s"
                }} />
            </span>
        </label>
    );
}

// --- Threshold Control with Box + Slider (Golden Yellow) ---
function ThresholdControl({
    value,
    label,
    description,
    valueType = "decimal",
    min = 0,
    max = null,
    onChange,
    hideSlider = false
}) {
    let sliderMax = max;
    let step = 0.01;
    let decimals = 2;

    if (valueType === "decimal") {
        if (sliderMax === null) sliderMax = 4;
        step = 0.01;
        decimals = 2;
    } else if (valueType === "integer") {
        if (sliderMax === null) sliderMax = 100;
        step = 1;
        decimals = 0;
    } else if (valueType === "string") {
        step = 1;
        decimals = 0;
    }

    const displayValue =
        valueType === "string"
            ? (value ?? "")
            : (value !== null && value !== undefined
                ? Number(value).toFixed(decimals)
                : "0.00");

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }} data-analytics={MODULE_NAME}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 140, fontWeight: 600, fontSize: 13 }}>{label}</div>
                <span
                    style={{
                        marginRight: 8,
                        verticalAlign: "middle",
                        cursor: "pointer",
                        color: "#888",
                        fontSize: 16
                    }}
                    title={description}
                >🛈</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {valueType === "string" ? (
                    <input
                        type="text"
                        value={value ?? ""}
                        onChange={e => onChange(e.target.value)}
                        style={{
                            width: 70,
                            background: "rgba(251, 192, 45, 0.15)",
                            color: "#fbc02d",
                            border: "1px solid #fbc02d",
                            borderRadius: 4,
                            padding: "6px 10px",
                            fontWeight: 600,
                            fontSize: 14,
                            textAlign: "center"
                        }}
                    />
                ) : (
                    <input
                        type="number"
                        value={value ?? 0}
                        min={min}
                        max={sliderMax}
                        step={step}
                        onChange={e => onChange(Number(e.target.value))}
                        style={{
                            width: 70,
                            background: "rgba(251, 192, 45, 0.15)",
                            color: "#fbc02d",
                            border: "1px solid #fbc02d",
                            borderRadius: 4,
                            padding: "6px 10px",
                            fontWeight: 600,
                            fontSize: 14,
                            textAlign: "center"
                        }}
                    />
                )}
                {!hideSlider && valueType !== "string" && (
                    <input
                        type="range"
                        min={min}
                        max={sliderMax}
                        step={step}
                        value={value ?? 0}
                        onChange={e => onChange(Number(e.target.value))}
                        style={{
                            flex: 1,
                            accentColor: "#fbc02d"
                        }}
                    />
                )}
                {valueType !== "string" && (
                    <span style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#fbc02d",
                        minWidth: 45,
                        textAlign: "center"
                    }}>
                        {displayValue}
                    </span>
                )}
            </div>
        </div>
    );
}

// --- Count Threshold Box with Slider (Blue) ---
function CountThresholdControl({
    label,
    value,
    description,
    min = 0,
    max = 100,
    onChange
}) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
                <span
                    style={{
                        cursor: "pointer",
                        color: "#888",
                        fontSize: 14
                    }}
                    title={description}
                >🛈</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input
                    type="number"
                    value={value ?? 0}
                    min={min}
                    max={max}
                    step={1}
                    onChange={e => onChange(Number(e.target.value))}
                    style={{
                        width: 70,
                        background: "rgba(25, 118, 210, 0.15)",
                        color: "#1976d2",
                        border: "1px solid #1976d2",
                        borderRadius: 4,
                        padding: "6px 10px",
                        fontWeight: 600,
                        fontSize: 14,
                        textAlign: "center"
                    }}
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={1}
                    value={value ?? 0}
                    onChange={e => onChange(Number(e.target.value))}
                    style={{
                        flex: 1,
                        accentColor: "#1976d2"
                    }}
                />
            </div>
        </div>
    );
}

export default function UserPatternSettingUpdate({
    patternId,
    countThreshold = {},
    thresholdDefinition = {},
    thresholdValues = {},
    patternMatches = null
}) {
    const [patterns, setPatterns] = useState([]);
    const [settings, setSettings] = useState({});
    const [countSettings, setCountSettings] = useState({});
    const [defaultLookbackWindow, setDefaultLookbackWindow] = useState(30);
    const [tooltip, setTooltip] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState("");
    const [loading, setLoading] = useState(true);

    // Add these hooks to track original values
    const [originalSettings, setOriginalSettings] = useState({});
    const [originalCountSettings, setOriginalCountSettings] = useState({});

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/user-pattern-settings`, {
            withCredentials: true
        })
            .then(res => {
                setPatterns(res.data.patterns || []);
                const settingsData = res.data.settings || {};

                const enrichedSettings = {};
                const enrichedCountSettings = {};

                Object.entries(settingsData).forEach(([patternIdKey, setting]) => {
                    enrichedSettings[patternIdKey] = {
                        ...setting,
                        json_threshold: setting.json_threshold || {}
                    };
                    enrichedCountSettings[patternIdKey] = {
                        threshold_emerging: setting.threshold_emerging,
                        threshold_confirmed: setting.threshold_confirmed,
                        threshold_strong: setting.threshold_strong
                    };
                });

                setSettings(enrichedSettings);
                setCountSettings(enrichedCountSettings);
                setOriginalSettings(enrichedSettings); // Store original for comparison
                setOriginalCountSettings(enrichedCountSettings); // Store original for comparison
                setTooltip(res.data.tooltip || "");
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching pattern settings:", err);
                setLoading(false);
            });
    }, []);

    const normalizedPatternId = patternId ? Number(patternId) : null;
    const activePattern = patterns.find(p => Number(p.pattern_id) === normalizedPatternId) || null;

    // Initialize settings with sensible defaults
    const activeSetting = settings[normalizedPatternId] || {
        enabled: 1,
        json_threshold: {}
    };

    const activeCountSetting = countSettings[normalizedPatternId] || {
        threshold_emerging: 5,
        threshold_confirmed: 10,
        threshold_strong: 15
    };

    // Get current values from json_threshold, fallback to thresholdValues, then to thresholdDefinition default
    const currentSettings = activeSetting.json_threshold || {};

    function updateSettingField(field, value) {
        setSettings(prev => ({
            ...prev,
            [normalizedPatternId]: {
                ...prev[normalizedPatternId],
                json_threshold: {
                    ...currentSettings,
                    [field]: value
                }
            }
        }));
    }

    function updateCountSettingField(field, value) {
        setCountSettings(prev => ({
            ...prev,
            [normalizedPatternId]: {
                ...prev[normalizedPatternId],
                [field]: value
            }
        }));
    }

    // Utility to check if any threshold or count threshold changed
    function hasCoreSettingsChanged() {
        const s = settings[normalizedPatternId] || activeSetting;
        const cs = countSettings[normalizedPatternId] || activeCountSetting;
        const origS = originalSettings[normalizedPatternId] || {};
        const origCS = originalCountSettings[normalizedPatternId] || {};

        // Check threshold fields
        let thresholdChanged = false;
        Object.keys(thresholdDefinition).forEach(key => {
            const curr = (s.json_threshold || {})[key];
            const orig = (origS.json_threshold || {})[key];
            if (curr !== orig) thresholdChanged = true;
        });

        // Check count thresholds
        let countChanged = false;
        ["threshold_emerging", "threshold_confirmed", "threshold_strong"].forEach(field => {
            if (cs[field] !== origCS[field]) countChanged = true;
        });

        return thresholdChanged || countChanged;
    }

    function handleSave() {
        if (!normalizedPatternId) {
            setSaveMsg("✗ Invalid pattern ID");
            return;
        }
        setSaving(true);
        setSaveMsg("");
        const s = settings[normalizedPatternId] || activeSetting;
        const cs = countSettings[normalizedPatternId] || activeCountSetting;

        // Build settings payload with ONLY threshold definitions
        const settingsPayload = {};
        Object.entries(thresholdDefinition).forEach(([key, threshold]) => {
            settingsPayload[key] = currentSettings[key] !== undefined
                ? currentSettings[key]
                : threshold.value;
        });

        // Determine if core settings changed
        const updateCoreSettingsFlag = hasCoreSettingsChanged();

        axios.post(`${process.env.REACT_APP_API_URL}/api/user-pattern-settings`, {
            pattern_id: normalizedPatternId,
            enabled: s.enabled ?? 1,
            settings: settingsPayload,
            threshold_emerging: cs.threshold_emerging,
            threshold_confirmed: cs.threshold_confirmed,
            threshold_strong: cs.threshold_strong,
            default_lookback_window: defaultLookbackWindow,
            update_core_settings_flag: updateCoreSettingsFlag
        }, { withCredentials: true })
            .then(res => {
                setSaveMsg("✓ Saved!");
                setTimeout(() => setSaveMsg(""), 1200);
                // Update originals after successful save
                setOriginalSettings(settings);
                setOriginalCountSettings(countSettings);
            })
            .catch(err => {
                console.error("Save error:", err);
                setSaveMsg("✗ Error saving");
            })
            .finally(() => setSaving(false));
    }

    const thresholdEntries = Object.entries(thresholdDefinition);
    const useGridLayout = thresholdEntries.length >= 3;
    const hideSliderForHours = (activePattern?.name || "").toLowerCase().includes("hour");

    if (loading) {
        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 420,
                color: "#fff",
                fontSize: 16
            }}>
                Loading pattern settings...
            </div>
        );
    }

    if (!normalizedPatternId || !activePattern) {
        return (
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 420,
                color: "#e53935",
                fontSize: 16
            }}>
                Pattern not found (ID: {patternId})
            </div>
        );
    }

    return (
        <div style={{
            display: "flex",
            flexDirection: "row",
            background: "#181A25",
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
            minHeight: 420,
            minWidth: 600,
            overflow: "hidden"
        }}>
            {/* Left Panel */}
            <div style={{
                background: "linear-gradient(135deg, #1e5a96 0%, #0f4c81 100%)",
                padding: "32px 24px",
                minWidth: 160,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                color: "#fff",
                justifyContent: "center"
            }}>
                <div style={{ fontSize: 64, fontWeight: 700, marginBottom: 16 }}>⚙️</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#00FFFF" }}>DEFAULT LOOKBACK</div>
                <input
                    type="number"
                    value={defaultLookbackWindow}
                    onChange={e => setDefaultLookbackWindow(Math.max(1, Number(e.target.value)))}
                    min={1}
                    max={365}
                    style={{
                        fontSize: 24,
                        fontWeight: 700,
                        marginTop: 8,
                        width: 90,
                        padding: "6px 8px",
                        background: "rgba(0, 255, 255, 0.1)",
                        color: "#00FFFF",
                        border: "2px solid #00FFFF",
                        borderRadius: 6,
                        textAlign: "center",
                        outline: "none",
                        transition: "box-shadow 0.2s"
                    }}
                    onFocus={e => e.target.style.boxShadow = "0 0 12px rgba(0, 255, 255, 0.4)"}
                    onBlur={e => e.target.style.boxShadow = "none"}
                />
                <div style={{ fontSize: 12, marginTop: 8, color: "#888" }}>days</div>
            </div>

            {/* Right Panel */}
            <div style={{
                flex: 1,
                padding: "32px 32px 24px 32px",
                color: "#fff",
                minWidth: 340,
                overflowY: "auto",
                maxHeight: 420
            }}>
                {/* Header */}
                <div
                    className={`pattern-title pattern-id-${normalizedPatternId}`}
                    style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
                >
                    <span style={{ fontWeight: 700, fontSize: 20, flex: 1 }}>
                        {activePattern.name}
                    </span>
                    <ToggleSwitch
                        checked={!!(activeSetting.enabled ?? 1)}
                        onChange={val => setSettings(prev => ({
                            ...prev,
                            [normalizedPatternId]: {
                                ...prev[normalizedPatternId],
                                enabled: val ? 1 : 0
                            }
                        }))}
                        label="Active"
                    />
                </div>
                <div style={{ color: "#aaa", fontSize: 14, marginBottom: 18 }}>
                    {activePattern.description}
                </div>
                <hr style={{ border: "1px solid #222", margin: "18px 0" }} />

                {/* Dynamic Threshold Definition */}
                {thresholdEntries.length > 0 && (
                    <>
                        <div style={{ marginBottom: 16 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: "#aaa" }}>
                                THRESHOLD SETTINGS
                            </span>
                        </div>

                        {useGridLayout ? (
                            /* Grid Layout (2 columns) for 4+ thresholds */
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "16px 24px",
                                marginBottom: 18
                            }}>
                                {thresholdEntries.map(([key, threshold]) => {
                                    const currentValue =
                                        currentSettings[key] !== undefined && currentSettings[key] !== null
                                            ? currentSettings[key]
                                            : (thresholdValues[key] !== undefined && thresholdValues[key] !== null
                                                ? thresholdValues[key]
                                                : threshold.value);

                                    return (
                                        <div key={key}>
                                            <ThresholdControl
                                                value={currentValue}
                                                label={threshold.label}
                                                description={threshold.description}
                                                valueType={threshold.value_type || "decimal"}
                                                min={0}
                                                max={threshold.value_type === "decimal" ? 4 : 100}
                                                onChange={val => updateSettingField(key, val)}
                                                hideSlider={true}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            /* Stacked Layout (1 column) for < 4 thresholds */
                            <div>
                                {thresholdEntries.map(([key, threshold]) => {
                                    const currentValue =
                                        currentSettings[key] !== undefined && currentSettings[key] !== null
                                            ? currentSettings[key]
                                            : (thresholdValues[key] !== undefined && thresholdValues[key] !== null
                                                ? thresholdValues[key]
                                                : threshold.value);

                                    return (
                                        <ThresholdControl
                                            key={key}
                                            value={currentValue}
                                            label={threshold.label}
                                            description={threshold.description}
                                            valueType={threshold.value_type || "decimal"}
                                            min={0}
                                            max={threshold.value_type === "decimal" ? 4 : 100}
                                            onChange={val => updateSettingField(key, val)}
                                            hideSlider={hideSliderForHours}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Dynamic Count Thresholds (editable) */}
                {Object.entries(countThreshold).length > 0 && (
                    <>
                        <div style={{ marginTop: 12, marginBottom: 16 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: "#aaa" }}>
                                PATTERN THRESHOLDS
                            </span>
                        </div>
                        {/* Order: emerging, confirmed, strong */}
                        {["emerging", "confirmed", "strong"].map(key => {
                            if (!countThreshold[key]) return null;
                            const fieldName = `threshold_${key}`;
                            const threshold = countThreshold[key];
                            const currentValue = activeCountSetting[fieldName] ?? threshold.value;
                            return (
                                <CountThresholdControl
                                    key={fieldName}
                                    label={threshold.label || key.charAt(0).toUpperCase() + key.slice(1)}
                                    value={currentValue}
                                    description={threshold.description}
                                    min={0}
                                    max={100}
                                    onChange={val => updateCountSettingField(fieldName, val)}
                                />
                            );
                        })}
                    </>
                )}

                {/* Save Button */}
                <div data-analytics={MODULE_NAME}>
                    <button
                        onClick={(e) => {
                            analyticsHandler({
                                event_type: "button_click",
                                module_name: MODULE_NAME,
                                subcomponent: "Save Settings",
                                screen: window.location.pathname,
                            })(e);
                            handleSave();
                        }}
                        disabled={saving}
                        style={{
                            background: "#00FFFF",
                            color: "#181A25",
                            fontWeight: 700,
                            fontSize: 16,
                            border: "none",
                            borderRadius: 6,
                            padding: "10px 32px",
                            marginTop: 18,
                            cursor: saving ? "not-allowed" : "pointer",
                            transition: "opacity 0.2s"
                        }}
                        onMouseEnter={e => !saving && (e.target.style.opacity = "0.9")}
                        onMouseLeave={e => !saving && (e.target.style.opacity = "1")}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                    {saveMsg && (
                        <span style={{
                            marginLeft: 18,
                            color: saveMsg.startsWith("✓") ? "#00FF99" : "#e53935",
                            fontWeight: 600,
                            fontSize: 14
                        }}>{saveMsg}</span>
                    )}
                </div>
            </div>
        </div>
    );
}